import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../../lib/api-client.js';

export interface Panel<T> {
  loading: boolean;
  error: string | null;
  data: T | null;
}

interface EmailHealthData {
  resend?: { configured: boolean };
  weekly_attendance_report?: EmailChannelBlock;
  onboarding_reminder_digest?: EmailChannelBlock;
  contact_form?: EmailChannelBlock;
  note?: string;
}

export interface EmailChannelBlock {
  from?: string;
  recipients?: string[];
  to?: string[];
  cron?: string;
  cron_description?: string;
  manual_trigger?: string;
  trigger?: string;
}

export interface FeatureFlag {
  id?: string | number;
  flag_name: string;
  enabled: boolean;
  scope?: string;
  terminal_id?: string;
  updated_at?: string;
  notes?: string;
}

function emptyPanel<T>(): Panel<T> {
  return { loading: true, error: null, data: null };
}

function errorPanel<T>(message: string): Panel<T> {
  return { loading: false, error: message || 'Unavailable', data: null };
}

function okPanel<T>(data: T): Panel<T> {
  return { loading: false, error: null, data };
}

async function fetchPanel<T>(path: string, pick: (data: unknown) => T): Promise<Panel<T>> {
  try {
    const data = await apiFetch(path);
    if (data && typeof data === 'object' && (data as Record<string, unknown>).error) {
      return errorPanel<T>(String((data as Record<string, unknown>).error));
    }
    return okPanel(pick(data));
  } catch (err) {
    return errorPanel<T>(err instanceof Error ? err.message : 'Network error');
  }
}

export function useSettingsReadModel() {
  const [email, setEmail] = useState<Panel<EmailHealthData>>(emptyPanel());
  const [flags, setFlags] = useState<Panel<FeatureFlag[]>>(emptyPanel());

  const load = useCallback(async () => {
    setEmail(emptyPanel());
    setFlags(emptyPanel());

    const [emailResult, flagsResult] = await Promise.all([
      fetchPanel<EmailHealthData>('/api/admin/email-health', (d) => d as EmailHealthData),
      fetchPanel<FeatureFlag[]>('/api/feature-flags', (d) => {
        const resp = d as { flags?: FeatureFlag[] };
        return Array.isArray(resp?.flags) ? resp.flags : [];
      }),
    ]);

    setEmail(emailResult);
    setFlags(flagsResult);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { email, flags, refreshSettings: load };
}
